import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Icon } from '@clientbase/clientbase-library'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'

import { generatePix } from '../SaasCheckout.service'

type PixPaymentProps = {
  billingUuid: string
}

const wait = (seconds: number) =>
  new Promise((resolve) => setTimeout(resolve, seconds * 1000))

// PIX da cobrança do checkout. Busca o código ao abrir (o PIX da conta de cobrança
// pode ser reemitido pelo banco, então não reaproveita um código de outra sessão),
// mas uma vez por cobrança: o drawer mantém esta aba montada ao trocar para o
// cartão, e o React em desenvolvimento roda o efeito duas vezes.
const PixPayment = ({ billingUuid }: PixPaymentProps) => {
  const [loading, setLoading] = useState(true)
  const [emv, setEmv] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const requestId = useRef(0)
  const loadedFor = useRef<string | null>(null)

  const load = useCallback(async () => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)

    let result = await generatePix(billingUuid)
    // Repetição recusada pela API (409): espera o tempo pedido e tenta uma vez.
    if (!result.ok && result.duplicate) {
      await wait(result.retryAfter)
      if (id !== requestId.current) return
      result = await generatePix(billingUuid)
    }

    // Resposta de uma chamada mais antiga não sobrescreve a mais nova.
    if (id !== requestId.current) return
    if (result.ok) {
      setEmv(result.emv)
      setError(null)
    } else {
      setEmv(null)
      setError(result.message)
    }
    setLoading(false)
  }, [billingUuid])

  useEffect(() => {
    if (loadedFor.current === billingUuid) return
    loadedFor.current = billingUuid
    load()
  }, [billingUuid, load])

  const copy = async () => {
    if (!emv) return
    try {
      await navigator.clipboard.writeText(emv)
      toast.success('Código PIX copiado')
    } catch {
      toast.error(
        'Não foi possível copiar. Selecione o código e copie manualmente.'
      )
    }
  }

  if (loading) {
    return (
      <Stack alignItems="center" py={4} gap={1.5}>
        <CircularProgress size={28} />
        <Typography variant="smLight">Gerando o PIX…</Typography>
      </Stack>
    )
  }

  if (error || !emv) {
    return (
      <Stack alignItems="center" py={3} gap={1.5}>
        <Typography variant="smLight" textAlign="center">
          {error || 'Não foi possível gerar o PIX.'}
        </Typography>
        <Button type="button" variantButton="neutral" onClick={load}>
          Tentar de novo
        </Button>
      </Stack>
    )
  }

  return (
    <Stack alignItems="center" gap={2}>
      <Typography variant="smLight" textAlign="center">
        Escaneie o QR Code no app do seu banco ou copie o código abaixo. A
        confirmação aparece aqui sozinha em alguns segundos.
      </Typography>
      <Box p={1.5} border="1px solid #E8EBE8" borderRadius="8px" bgcolor="#fff">
        <QRCodeSVG value={emv} size={200} level="M" />
      </Box>
      <Box
        onClick={copy}
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          backgroundColor: '#F2F5F2',
          borderRadius: '8px',
          padding: '8px 12px'
        }}
      >
        <Typography
          variant="smLight"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {emv}
        </Typography>
        <Icon icon="contentCopy" size={3} />
      </Box>
      <Button type="button" fullWidth onClick={copy}>
        Copiar código PIX
      </Button>
    </Stack>
  )
}

export default PixPayment
